import React, { Component } from 'react';
import { withRouter } from 'react-router';
import moment from 'moment';

import SurveyApi from 'apis/Survey';
import { insertIdtoUrl, ROUTES } from 'constants/Routes';
import { getBusinessPlan, getRole } from 'constants/localStorageManager';
import { USERS } from 'constants/Users';
import 'assets/scss/components/customSurvey.css';

import SurveyEditForm from './EditForm';
import Toaster from '../Toaster';

const formatCustomTerms = str => {
  const splitSentences = str.match(/\b((?!=|\.).)+(.)\b/g);
  const formattedTerm = splitSentences.filter(
    sentence =>
      !(
        sentence.toLowerCase().includes('voucher expire') ||
        sentence.toLowerCase().includes('limit one')
      )
  );
  return formattedTerm.join('. ');
};

const addExpiryDateToVoucher = (str, date, limitVoucher) => {
  const limitText = limitVoucher ? 'Limit one (1) voucher per customer. ' : '';
  return `${limitText}Voucher expires on ${date.calendar()}. ${str}`;
};

const termsExpiryDate = moment()
  .add(1, 'years')
  .calendar();

const DEFAULT_TERMS = `Reward terms are subject to change at the discretion of participating
businesses. Can only be redeemed at participating stores`;

const noLocationDetails = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
};

class CustomSurveyForm extends Component {
  constructor() {
    super();
    this.state = {
      customSurvey: {},
      addonActive: false,
      isLoading: false,
    };
  }

  componentDidMount() {
    this.fetchCustomSurveyDetails();
  }

  onCancel = () => {
    const {
      history,
      match: { params },
    } = this.props;
    const { id: businessId } = params;

    history.push(insertIdtoUrl(ROUTES.CUSTOM_SURVEY_LIST, businessId));
  };

  fetchCustomSurveyDetails = () => {
    const {
      match: { params },
    } = this.props;
    const { customSurveyId } = params;

    if (!customSurveyId) return null;
    this.setState({ isLoading: true });

    SurveyApi.getDetails(customSurveyId).then(({ data }) => {
      const {
        name: customSurveyName,
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        city,
        state,
        zip,
        primary_number: phone,
        rewards: rewardsAttributes,
        international,
        custom_terms,
        location_code: locationCode,
        addon_active,
        default_addon,
        max_recurring_allowed,
        recurring_frequency,
        social_medias_active: socialMediasEnabled,
        social_reviews_active: socialReviewsEnabled,
      } = data;

      const customTerms = custom_terms
        ? formatCustomTerms(custom_terms)
        : DEFAULT_TERMS;

      const rewardObj = rewardsAttributes && rewardsAttributes[0];
      const reward = rewardObj && rewardObj.title;
      const rewardId = rewardObj && rewardObj.id;
      const date = rewardObj && rewardObj.expiry_date;
      const expiryDate = date ? moment(date) : moment(termsExpiryDate);
      const locationCheckBox = !!addressLine1;
      const rewardCheckbox = !!reward;
      const defaultCustomTerms = customTerms;

      const userPlan = getBusinessPlan();
      const isSuperAdmin = getRole() === USERS.ADMIN;
      const isProPlan = userPlan.title === 'pro';
      const isEssentialPlan = userPlan.title === 'essential';

      const addonActive =
        addon_active && (isProPlan || isSuperAdmin || isEssentialPlan);
      const addonCheckbox = !!default_addon && addonActive;
      const customSurvey = {
        ...data,
        customSurveyName,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        phone,
        reward,
        international,
        customTerms,
        locationCode,
        locationCheckBox,
        rewardCheckbox,
        defaultCustomTerms,
        customizeTerms: false,
        default_addon,
        addonCheckbox,
        expiryDate,
        recurring_frequency: recurring_frequency || 'weekly',
        max_recurring_allowed: {
          label: max_recurring_allowed || 1,
          value: max_recurring_allowed || 1,
        },
      };

      this.setState({
        customSurvey,
        rewardId,
        addonActive,
        socialMediasEnabled,
        socialReviewsEnabled,
        isLoading: false,
      });
    });
  };

  onSubmit = values => {
    this.saveChanges(values);
  };

  reFormatValues = values => {
    const {
      match: { params },
    } = this.props;
    const { customSurveyId } = params;
    const { rewardId } = this.state;
    const {
      customSurveyName,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      phone,
      reward,
      international,
      customTerms,
      locationCheckBox,
      rewardCheckbox,
      addonCheckbox,
      default_addon,
      expiryDate,
      is_survey_recurring,
      recurring_frequency,
      max_recurring_allowed,
      limit_voucher_per_customer,
    } = values;
    const rewardTitle = rewardCheckbox ? reward : '';
    const formattedCustomTerm = customTerms
      ? addExpiryDateToVoucher(
          customTerms,
          expiryDate,
          limit_voucher_per_customer
        )
      : customTerms;

    const addressDetails = locationCheckBox
      ? {
          addressLine1,
          addressLine2,
          city,
          state,
          zip,
          phone,
        }
      : noLocationDetails;
    const rewardAttributes = [
      { id: rewardId, title: rewardTitle, expiry_date: expiryDate },
    ];
    const isSoonoSurveyPaused = false;

    const {
      addressLine1: address_line_1,
      addressLine2: address_line_2,
      city: _city,
      state: _state,
      zip: _zip,
      phone: _phone,
    } = addressDetails;
    const addonType = addonCheckbox ? default_addon : null;

    return {
      customSurveyId,
      customSurveyName,
      address_line_1,
      address_line_2,
      city: _city,
      state: _state,
      zip: _zip,
      phone: _phone,
      isSoonoSurveyPaused,
      rewardAttributes,
      international,
      customTerms: formattedCustomTerm,
      addonType,
      is_survey_recurring,
      recurring_frequency,
      max_recurring_allowed: max_recurring_allowed.value,
      limit_voucher_per_customer,
    };
  };

  saveChanges = values => {
    const customSurveyValues = this.reFormatValues(values);

    const {
      customSurveyId,
      customSurveyName,
      address_line_1,
      address_line_2,
      city,
      state,
      zip,
      phone,
      isSoonoSurveyPaused,
      rewardAttributes,
      international,
      customTerms,
      addonType,
      is_survey_recurring,
      recurring_frequency,
      max_recurring_allowed,
      limit_voucher_per_customer,
    } = customSurveyValues;

    if (!customSurveyId) return this.addNewSurvey(customSurveyValues);

    this.setState({ isLoading: true });

    SurveyApi.update(
      customSurveyId,
      customSurveyName,
      address_line_1,
      address_line_2,
      city,
      state,
      zip,
      phone,
      isSoonoSurveyPaused,
      rewardAttributes,
      international,
      customTerms,
      addonType,
      is_survey_recurring,
      recurring_frequency,
      max_recurring_allowed,
      limit_voucher_per_customer
    )
      .then(({ data }) => {
        this.fetchCustomSurveyDetails();
        this.onCancel();
        Toaster.getSuccessToaster(
          'check-circle',
          'Successfully updated custom survey'
        );
      })
      .catch(e =>
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Failed to update custom survey'
        )
      )
      .then(() => {
        this.setState({ isLoading: false });
      });
  };

  addNewSurvey = values => {
    const { addNewSurvey } = this.props;
    addNewSurvey(values);
  };

  render() {
    const {
      customSurvey,
      addonActive,
      socialMediasEnabled,
      socialReviewsEnabled,
      isLoading,
    } = this.state;

    const { defaultCustomSurveyName, addForm, isSavingSurvey } = this.props;

    const initialValues = {
      customTerms: DEFAULT_TERMS,
      defaultCustomTerms: DEFAULT_TERMS,
      customSurveyName: defaultCustomSurveyName,
      expiryDate: moment(termsExpiryDate),
      recurring_frequency: 'weekly',
      max_recurring_allowed: { label: 1, value: 1 },
      ...customSurvey,
    };

    return (
      <div className="container">
        <SurveyEditForm
          initialValues={initialValues}
          addonActive={addonActive}
          onSubmit={this.onSubmit}
          onCancel={this.onCancel}
          isLoading={isLoading || isSavingSurvey}
          socialMediasEnabled={socialMediasEnabled}
          socialReviewsEnabled={socialReviewsEnabled}
          editForm={!addForm}
        />
      </div>
    );
  }
}

export default withRouter(CustomSurveyForm);



// WEBPACK FOOTER //
// ./src/components/customSurvey/CustomSurveyForm.js